/**
 * Flag Animation - creates realistic waving flag effects
 * Uses vertex shader animation for cloth-like movement
 * Supports multiple flags with different wind conditions
 */

import * as THREE from 'three';
import type { FactionId } from '@/types';
import { FACTIONS } from '@/game/factions/FactionData';

interface Flag {
  id: string;
  mesh: THREE.Mesh;
  polePosition: THREE.Vector3;
  factionId: FactionId;
  size: 'small' | 'medium' | 'large';
  windPhase: number;
}

export class FlagAnimation {
  private scene: THREE.Scene;
  private flags: Map<string, Flag> = new Map();
  private time: number = 0;
  private windDirection: THREE.Vector2 = new THREE.Vector2(1, 0.3);
  private windStrength: number = 1.0;

  // Shared materials per faction
  private flagMaterials: Map<FactionId, THREE.ShaderMaterial> = new Map();

  constructor(scene: THREE.Scene) {
    this.scene = scene;
  }

  private createFlagMaterial(factionId: FactionId): THREE.ShaderMaterial {
    // Check cache
    if (this.flagMaterials.has(factionId)) {
      return this.flagMaterials.get(factionId)!.clone();
    }

    const faction = FACTIONS[factionId];
    const mainColor = new THREE.Color(faction.color);
    const accentColor = new THREE.Color(faction.secondaryColor);

    const material = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uWindDirection: { value: this.windDirection },
        uWindStrength: { value: this.windStrength },
        uMainColor: { value: mainColor },
        uAccentColor: { value: accentColor },
        uPhase: { value: 0 },
      },
      vertexShader: `
        uniform float uTime;
        uniform vec2 uWindDirection;
        uniform float uWindStrength;
        uniform float uPhase;

        varying vec2 vUv;
        varying float vWave;

        void main() {
          vUv = uv;

          vec3 pos = position;

          // Wave effect based on horizontal position (more movement at the flag end)
          float waveAmount = uv.x; // 0 at pole, 1 at end
          float waveFreq = 3.0;
          float waveSpeed = 4.0;

          // Primary wave
          float wave1 = sin(uv.x * waveFreq + uTime * waveSpeed + uPhase) * waveAmount;

          // Secondary wave (faster, smaller)
          float wave2 = sin(uv.x * waveFreq * 2.0 + uTime * waveSpeed * 1.5 + uPhase * 0.7) * waveAmount * 0.3;

          // Vertical ripple
          float wave3 = sin(uv.y * 5.0 + uTime * 3.0) * waveAmount * 0.1;

          float totalWave = (wave1 + wave2 + wave3) * uWindStrength * 0.15;
          vWave = totalWave;

          // Apply wave to z position (forward/backward movement)
          pos.z += totalWave;

          // Slight y movement (flag dips in wind)
          pos.y -= abs(totalWave) * 0.3 * waveAmount;

          // Stretch effect at peaks
          pos.x += totalWave * 0.05;

          gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 uMainColor;
        uniform vec3 uAccentColor;
        uniform float uTime;

        varying vec2 vUv;
        varying float vWave;

        void main() {
          // Create faction emblem pattern (simple stripe or gradient)
          float pattern = 0.0;

          // Horizontal stripe
          if (vUv.y > 0.35 && vUv.y < 0.65) {
            pattern = 1.0;
          }

          // Mix colors
          vec3 color = mix(uMainColor, uAccentColor, pattern);

          // Add shading based on wave (simulates light)
          float shade = 0.9 + vWave * 2.0;
          color *= shade;

          // Edge darkening for depth
          float edgeDark = smoothstep(0.0, 0.1, vUv.y) * smoothstep(1.0, 0.9, vUv.y);
          color *= 0.8 + edgeDark * 0.2;

          // Fabric texture (subtle noise effect)
          float noise = fract(sin(dot(vUv * 50.0, vec2(12.9898, 78.233))) * 43758.5453);
          color += (noise - 0.5) * 0.03;

          gl_FragColor = vec4(color, 1.0);
        }
      `,
      side: THREE.DoubleSide,
    });

    this.flagMaterials.set(factionId, material);
    return material;
  }

  private createFlagGeometry(size: 'small' | 'medium' | 'large'): THREE.PlaneGeometry {
    const sizes = {
      small: { width: 0.4, height: 0.25, segments: 12 },
      medium: { width: 0.7, height: 0.45, segments: 16 },
      large: { width: 1.0, height: 0.65, segments: 20 },
    };

    const { width, height, segments } = sizes[size];
    const geometry = new THREE.PlaneGeometry(width, height, segments, Math.floor(segments * 0.6));

    // Offset geometry so pole attachment is at origin
    geometry.translate(width / 2, 0, 0);

    return geometry;
  }

  addFlag(
    id: string,
    x: number,
    y: number,
    z: number,
    factionId: FactionId,
    size: 'small' | 'medium' | 'large' = 'medium'
  ): THREE.Group {
    if (this.flags.has(id)) {
      const existing = this.flags.get(id)!;
      return existing.mesh.parent as THREE.Group;
    }

    const group = new THREE.Group();
    group.position.set(x, y, z);

    // Create flag pole
    const poleHeight = size === 'large' ? 2.0 : size === 'medium' ? 1.2 : 0.8;
    const poleGeometry = new THREE.CylinderGeometry(0.025, 0.03, poleHeight, 6);
    const poleMaterial = new THREE.MeshStandardMaterial({
      color: 0x5d4037,
      roughness: 0.8,
    });
    const pole = new THREE.Mesh(poleGeometry, poleMaterial);
    pole.position.y = poleHeight / 2;
    pole.castShadow = true;
    group.add(pole);

    // Pole top ornament
    const topGeometry = new THREE.SphereGeometry(0.04, 6, 4);
    const topMaterial = new THREE.MeshStandardMaterial({
      color: 0xd4af37,
      roughness: 0.3,
      metalness: 0.6,
    });
    const top = new THREE.Mesh(topGeometry, topMaterial);
    top.position.y = poleHeight;
    group.add(top);

    // Create flag
    const flagGeometry = this.createFlagGeometry(size);
    const flagMaterial = this.createFlagMaterial(factionId);
    flagMaterial.uniforms.uPhase.value = Math.random() * Math.PI * 2;

    const flagMesh = new THREE.Mesh(flagGeometry, flagMaterial);
    flagMesh.position.y = poleHeight - 0.1;
    flagMesh.castShadow = true;
    group.add(flagMesh);

    this.scene.add(group);

    const flag: Flag = {
      id,
      mesh: flagMesh,
      polePosition: new THREE.Vector3(x, y, z),
      factionId,
      size,
      windPhase: Math.random() * Math.PI * 2,
    };

    this.flags.set(id, flag);

    return group;
  }

  removeFlag(id: string): void {
    const flag = this.flags.get(id);
    if (!flag) return;

    const group = flag.mesh.parent;
    if (group) {
      this.scene.remove(group);
      group.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.geometry.dispose();
          if (child.material instanceof THREE.Material) {
            child.material.dispose();
          }
        }
      });
    }

    this.flags.delete(id);
  }

  setWindDirection(x: number, z: number): void {
    this.windDirection.set(x, z).normalize();

    // Update all materials
    for (const material of this.flagMaterials.values()) {
      material.uniforms.uWindDirection.value = this.windDirection;
    }
  }

  setWindStrength(strength: number): void {
    this.windStrength = Math.max(0.2, Math.min(2.0, strength));

    // Update all materials
    for (const material of this.flagMaterials.values()) {
      material.uniforms.uWindStrength.value = this.windStrength;
    }
  }

  update(deltaTime: number): void {
    this.time += deltaTime;

    // Slowly vary wind direction and strength for natural effect
    const windVariation = Math.sin(this.time * 0.3) * 0.1;
    this.windDirection.x = 1 + windVariation;
    this.windDirection.y = 0.3 + Math.sin(this.time * 0.5) * 0.2;

    const strengthVariation = 0.9 + Math.sin(this.time * 0.7) * 0.2;

    for (const flag of this.flags.values()) {
      const material = flag.mesh.material as THREE.ShaderMaterial;
      if (material.uniforms) {
        material.uniforms.uTime.value = this.time;
        material.uniforms.uWindDirection.value = this.windDirection;
        material.uniforms.uWindStrength.value = this.windStrength * strengthVariation;

        // Rotate flag group to face wind direction
        const group = flag.mesh.parent;
        if (group) {
          const targetRotation = Math.atan2(this.windDirection.y, this.windDirection.x);
          group.rotation.y = THREE.MathUtils.lerp(
            group.rotation.y,
            targetRotation,
            deltaTime * 2
          );
        }
      }
    }
  }

  getFlagCount(): number {
    return this.flags.size;
  }

  clear(): void {
    for (const id of [...this.flags.keys()]) {
      this.removeFlag(id);
    }
  }

  dispose(): void {
    this.clear();

    // Dispose shared materials
    for (const material of this.flagMaterials.values()) {
      material.dispose();
    }
    this.flagMaterials.clear();
  }
}
